import { useCallback, useState } from "react";
import { Clock, FastForward } from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { FAST_FORWARD_PRESETS } from "@shared-core/data/journey-testing-types";
import {
  formatClockOffset,
  formatPreviewTime,
} from "@shared-core/domain/preview-clock";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../app/components/primitives/alert-dialog";
import { describeError, useLoaded } from "./consoleState";

/**
 * The console's shared header (07 §1): the preview's time, how far it has been moved on, and Fast-forward (1 day,
 * 3 days, 1 week, custom), which asks first — everyone in this preview moves forward, and the lifecycle jobs run.
 */
export function ConsoleClockHeader({
  onAdvanced,
}: {
  onAdvanced?: () => void;
}) {
  const adapter = useDataAdapter();
  const loadClock = useCallback(() => adapter.getPreviewClock(), [adapter]);
  const [clock, reloadClock] = useLoaded(loadClock);
  const [pendingDays, setPendingDays] = useState<number | null>(null);
  const [customDays, setCustomDays] = useState("");
  const [advancing, setAdvancing] = useState(false);

  const customValid =
    /^\d+$/.test(customDays) &&
    Number(customDays) >= 1 &&
    Number(customDays) <= 90;

  async function advance(days: number) {
    setAdvancing(true);
    try {
      const result = await adapter.advancePreviewClock({ days });
      const failed = result.job_runs.filter((run) => !run.ok).length;
      toast.success(
        `Preview moved to ${formatPreviewTime(result.now)}. ${result.job_runs.length} jobs ran` +
          (failed ? `, ${failed} failed (see Jobs).` : "."),
      );
      reloadClock();
      onAdvanced?.();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setAdvancing(false);
      setPendingDays(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <Clock className="size-5 text-muted-foreground" />
        <div>
          <div className="text-sm text-muted-foreground">Preview time</div>
          {clock.status === "loaded" ? (
            <div className="font-medium" data-testid="preview-time">
              {formatPreviewTime(clock.data.now)}{" "}
              <span className="text-muted-foreground">
                · {formatClockOffset(clock.data.offset_seconds)}
              </span>
            </div>
          ) : (
            <div className="text-muted-foreground">
              {clock.status === "loading"
                ? "Reading the clock…"
                : "Clock unavailable"}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {FAST_FORWARD_PRESETS.map((preset) => (
          <Button
            key={preset.days}
            variant="outline"
            size="sm"
            disabled={advancing}
            onClick={() => setPendingDays(preset.days)}
          >
            <FastForward className="size-4" />
            {preset.label}
          </Button>
        ))}
        <div className="flex items-end gap-2">
          <div className="grid gap-1">
            <Label htmlFor="fast-forward-days" className="text-xs">
              Custom days
            </Label>
            <Input
              id="fast-forward-days"
              className="h-8 w-20"
              inputMode="numeric"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value.trim())}
              aria-invalid={customDays !== "" && !customValid}
            />
          </div>
          <Button
            size="sm"
            disabled={advancing || !customValid}
            onClick={() => setPendingDays(Number(customDays))}
          >
            Fast-forward
          </Button>
        </div>
      </div>
      <AlertDialog
        open={pendingDays !== null}
        onOpenChange={(open) => !open && setPendingDays(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Fast-forward {pendingDays} day{pendingDays === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Everyone in this preview moves forward. The lifecycle jobs
              (reminders, expiries, invoicing, Xero) then run once at the new
              time. The clock never goes back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={advancing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={advancing}
              onClick={() => pendingDays && void advance(pendingDays)}
            >
              Move everyone forward
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
